// src/firebase/dataService.js
// ─────────────────────────────────────────────────────────────────────────────
// All Firestore CRUD operations.
//
// COLLECTIONS:
//   projects, certificates, experience — public read, ordered
//   posts                              — public read (visible only), createdAt-desc feed (blog)
//   profile, about, skills, config     — public read, single-doc
//   resume                             — admin only
//   resumeApplications, contactSubmissions, adminNotes — admin only
//   postComments, postReactions, postFeedback — blog engagement (see below)
//   jobSearchSettings                  — admin only (Job Assist preferences + last run)
//
// EXPERIENCE:
//   Stored as a dedicated Firestore collection (same pattern as projects).
//   Each document: { role, company, location, startDate, endDate, current,
//                    bullets[], order, visible }
//   getExperience()      — public, visible only, ordered
//   getExperienceAdmin() — all, ordered
//   Resume data builder merges the experience collection into the resume payload
//   so ResumeGenerator always gets live experience data.
// ─────────────────────────────────────────────────────────────────────────────
import {
  collection, getDocs, addDoc, updateDoc,
  deleteDoc, doc, orderBy, query, serverTimestamp, setDoc, getDoc, writeBatch,
  where, limit, increment,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "./config";
import fallback from "../data/content.json";
import { buildJobDocId, buildJobSourceDocId, hashString } from "../shared/jobIds";

// ─── IMAGE UPLOAD ─────────────────────────────────────────────────────────────
const STORAGE_PROVIDER = process.env.REACT_APP_STORAGE_PROVIDER || "cloudinary";
const CLOUD_NAME       = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const UPLOAD_PRESET    = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

const uploadViaCloudinary = async (file, folder) => {
  if (!CLOUD_NAME || !UPLOAD_PRESET) throw new Error("Cloudinary not configured.");
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", UPLOAD_PRESET);
  formData.append("folder", folder);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, { method:"POST", body:formData });
  if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(`Cloudinary upload failed (${res.status}): ` + (e?.error?.message || res.statusText)); }
  return (await res.json()).secure_url;
};

const uploadViaFirebase = async (file, folder) => {
  const storageRef = ref(storage, `${folder}/${Date.now()}_${file.name}`);
  const snapshot   = await Promise.race([
    uploadBytes(storageRef, file),
    new Promise((_, reject) => setTimeout(() => reject(new Error("Upload timed out.")), 20_000)),
  ]);
  return getDownloadURL(snapshot.ref);
};

const uploadImage = async (file, folder) =>
  STORAGE_PROVIDER === "firebase"
    ? uploadViaFirebase(file, folder)
    : uploadViaCloudinary(file, folder);

// ─── ORDER HELPERS ────────────────────────────────────────────────────────────
const readAllOrdered = async (colName) => {
  const q    = query(collection(db, colName), orderBy("order", "asc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

const reorderCollection = async (colName, sortedItems) => {
  const batch = writeBatch(db);
  sortedItems.forEach((item, idx) => {
    batch.update(doc(db, colName, item.id), { order: idx + 1, updatedAt: serverTimestamp() });
  });
  await batch.commit();
};

const insertAtOrder = async (colName, data, targetOrder) => {
  const existing   = await readAllOrdered(colName);
  const clampedPos = Math.max(1, Math.min(targetOrder, existing.length + 1));
  const shifted    = existing.filter(item => item.order >= clampedPos);

  const newRef = await addDoc(collection(db, colName), {
    ...data,
    order:     clampedPos,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  if (shifted.length > 0) {
    const batch = writeBatch(db);
    shifted.forEach(item => {
      batch.update(doc(db, colName, item.id), { order: item.order + 1, updatedAt: serverTimestamp() });
    });
    await batch.commit();
  }
  return newRef;
};

const moveToOrder = async (colName, targetId, newOrder) => {
  const all    = await readAllOrdered(colName);
  const others = all.filter(item => item.id !== targetId);
  const target = all.find(item => item.id === targetId);
  if (!target) return;
  const clampedPos = Math.max(1, Math.min(newOrder, all.length));
  others.splice(clampedPos - 1, 0, { ...target, order: clampedPos });
  await reorderCollection(colName, others);
};

export const resequenceCollection = async (colName) => {
  const all = await readAllOrdered(colName);
  await reorderCollection(colName, all);
};

// ─── PROJECTS ─────────────────────────────────────────────────────────────────

export const getProjects = async () => {
  try {
    const q    = query(collection(db, "projects"), orderBy("order", "asc"));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.visible !== false);
  } catch {
    return (fallback.projects || []).filter(p => p.visible !== false);
  }
};

export const getProjectsAdmin = async () => {
  try { return await readAllOrdered("projects"); }
  catch { return fallback.projects || []; }
};

export const addProject = async (data, imageFile) => {
  let imgsrc = data.imgsrc || "";
  if (imageFile) imgsrc = await uploadImage(imageFile, "portfolio/projects");
  const { order, ...rest } = data;
  return insertAtOrder("projects", {
    ...rest,
    imgsrc,
    visible:       data.visible       !== false,
    resumeVisible: data.resumeVisible !== false,
    stack:         data.stack         || "",
    bullets:       Array.isArray(data.bullets) ? data.bullets : [],
  }, Number(order) || 1);
};

export const updateProject = async (id, data, imageFile) => {
  let imgsrc = data.imgsrc || "";
  if (imageFile) imgsrc = await uploadImage(imageFile, "portfolio/projects");
  await updateDoc(doc(db, "projects", id), { ...data, imgsrc, updatedAt: serverTimestamp() });
  if (data.order != null) await moveToOrder("projects", id, Number(data.order));
};

export const deleteProject = async (id) => {
  await deleteDoc(doc(db, "projects", id));
  await resequenceCollection("projects");
};

export const setProjectVisibility = async (id, visible) =>
  updateDoc(doc(db, "projects", id), { visible, updatedAt: serverTimestamp() });

export const setProjectResumeVisibility = async (id, resumeVisible) =>
  updateDoc(doc(db, "projects", id), { resumeVisible, updatedAt: serverTimestamp() });

export const getProjectById = async (id) => {
  try {
    const snap = await getDoc(doc(db, "projects", id));
    if (snap.exists()) return { id: snap.id, ...snap.data() };
  } catch {}
  return (fallback.projects || []).find(p => p.id === id) || null;
};

// ─── EXPERIENCE ───────────────────────────────────────────────────────────────

/** PUBLIC — visible only, most-recent-first (order asc = chronological display order set by admin) */
export const getExperience = async () => {
  try {
    const q    = query(collection(db, "experience"), orderBy("order", "asc"));
    const snap = await getDocs(q);
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(e => e.visible !== false);
  } catch {
    return (fallback.resume?.experience || []).filter(e => e.visible !== false);
  }
};

/** ADMIN — all, ordered */
export const getExperienceAdmin = async () => {
  try { return await readAllOrdered("experience"); }
  catch { return fallback.resume?.experience || []; }
};

export const addExperience = async (data) => {
  const { order, ...rest } = data;
  return insertAtOrder("experience", {
    ...rest,
    category: data.category || "professional",
    visible: data.visible !== false,
    bullets: Array.isArray(data.bullets) ? data.bullets : [],
    current: !!data.current,
  }, Number(order) || 1);
};

export const updateExperience = async (id, data) => {
  await updateDoc(doc(db, "experience", id), {
    ...data,
    category: data.category || "professional",
    bullets: Array.isArray(data.bullets) ? data.bullets : [],
    current: !!data.current,
    updatedAt: serverTimestamp(),
  });
  if (data.order != null) await moveToOrder("experience", id, Number(data.order));
};

export const deleteExperience = async (id) => {
  await deleteDoc(doc(db, "experience", id));
  await resequenceCollection("experience");
};

export const setExperienceVisibility = async (id, visible) =>
  updateDoc(doc(db, "experience", id), { visible, updatedAt: serverTimestamp() });

// ─── CERTIFICATES ─────────────────────────────────────────────────────────────

export const getCertificates = async () => {
  try {
    const q    = query(collection(db, "certificates"), orderBy("order", "asc"));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => c.visible !== false);
  } catch {
    return (fallback.certificates || []).filter(c => c.visible !== false);
  }
};

export const getCertificatesAdmin = async () => {
  try { return await readAllOrdered("certificates"); }
  catch { return fallback.certificates || []; }
};

export const addCertificate = async (data, imageFile) => {
  let imgsrc = data.imgsrc || "";
  if (imageFile) imgsrc = await uploadImage(imageFile, "portfolio/certificates");
  const { order, ...rest } = data;
  return insertAtOrder("certificates", { ...rest, imgsrc, visible: data.visible !== false }, Number(order) || 1);
};

export const updateCertificate = async (id, data, imageFile) => {
  let imgsrc = data.imgsrc || "";
  if (imageFile) imgsrc = await uploadImage(imageFile, "portfolio/certificates");
  await updateDoc(doc(db, "certificates", id), { ...data, imgsrc, updatedAt: serverTimestamp() });
  if (data.order != null) await moveToOrder("certificates", id, Number(data.order));
};

export const deleteCertificate = async (id) => {
  await deleteDoc(doc(db, "certificates", id));
  await resequenceCollection("certificates");
};

export const setCertificateVisibility = async (id, visible) =>
  updateDoc(doc(db, "certificates", id), { visible, updatedAt: serverTimestamp() });

export const getCertificateById = async (id) => {
  try {
    const snap = await getDoc(doc(db, "certificates", id));
    if (snap.exists()) return { id: snap.id, ...snap.data() };
  } catch {}
  return (fallback.certificates || []).find(c => c.id === id) || null;
};

// ─── BLOG POSTS ───────────────────────────────────────────────────────────────

export const getPosts = async () => {
  try {
    const q    = query(collection(db, "posts"), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.visible !== false);
  } catch { return []; }
};

export const getPostsAdmin = async () => {
  try {
    const q    = query(collection(db, "posts"), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch { return []; }
};

export const getPostBySlug = async (slug) => {
  try {
    const q    = query(collection(db, "posts"), where("slug", "==", slug), limit(1));
    const snap = await getDocs(q);
    if (!snap.empty) { const d = snap.docs[0]; return { id: d.id, ...d.data() }; }
  } catch {}
  return null;
};

// Inline images dropped into a post body — same folder as cover images.
export const uploadBlogImage = (file) => uploadImage(file, "portfolio/blog");

export const addPost = async (data, imageFile) => {
  let coverImage = data.coverImage || "";
  if (imageFile) coverImage = await uploadImage(imageFile, "portfolio/blog");
  const visible = data.visible !== false;
  return addDoc(collection(db, "posts"), {
    ...data,
    coverImage,
    visible,
    tags: Array.isArray(data.tags) ? data.tags : [],
    createdAt:   serverTimestamp(),
    updatedAt:   serverTimestamp(),
    publishedAt: visible ? serverTimestamp() : null,
  });
};

export const updatePost = async (id, data, imageFile) => {
  let coverImage = data.coverImage || "";
  if (imageFile) coverImage = await uploadImage(imageFile, "portfolio/blog");
  await updateDoc(doc(db, "posts", id), {
    ...data,
    coverImage,
    tags: Array.isArray(data.tags) ? data.tags : [],
    updatedAt: serverTimestamp(),
  });
};

export const deletePost = async (id) => deleteDoc(doc(db, "posts", id));

// ─── BLOG ENGAGEMENT ──────────────────────────────────────────────────────────
// postComments  — public can CREATE (always status "pending") and READ only
//                 approved ones; admin moderates (approve / hide / reply / delete).
//   { postId, postSlug, postTitle, name, body, status: "pending"|"approved"|"hidden",
//     createdAt, reply?, repliedAt? }
// postReactions/{postId} — public counters { up, down }; firestore.rules only
//                 allows ±1 changes, so a visitor can vote, switch or undo.
//                 One-vote-per-browser is enforced client-side (localStorage).
// postFeedback  — private "what could be better?" notes, create-only for the
//                 public, read/manage by admin.
//   { postId, postSlug, postTitle, helpful: bool|null, message, read, createdAt }

export const getApprovedComments = async (postId) => {
  // Equality-only filters → no composite index needed; sort client-side.
  const q = query(collection(db, "postComments"), where("postId", "==", postId), where("status", "==", "approved"));
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
};

export const addPostComment = async ({ post, name, body }) =>
  addDoc(collection(db, "postComments"), {
    postId: post.id,
    postSlug: post.slug || "",
    postTitle: post.title || "",
    name: name.trim().slice(0, 60),
    body: body.trim().slice(0, 2000),
    status: "pending",
    createdAt: serverTimestamp(),
  });

export const getPostReactions = async (postId) => {
  const snap = await getDoc(doc(db, "postReactions", postId));
  const d = snap.exists() ? snap.data() : {};
  return { up: d.up || 0, down: d.down || 0 };
};

/** delta: { up?: 1|-1, down?: 1|-1 } — e.g. switching a vote is { up: 1, down: -1 }. */
export const changePostReaction = async (postId, delta) => {
  const update = {};
  if (delta.up) update.up = increment(delta.up);
  if (delta.down) update.down = increment(delta.down);
  return setDoc(doc(db, "postReactions", postId), update, { merge: true });
};

export const addPostFeedback = async ({ post, helpful, message }) =>
  addDoc(collection(db, "postFeedback"), {
    postId: post.id,
    postSlug: post.slug || "",
    postTitle: post.title || "",
    helpful: typeof helpful === "boolean" ? helpful : null,
    message: message.trim().slice(0, 2000),
    read: false,
    createdAt: serverTimestamp(),
  });

// Admin side
export const getAllPostComments = async () => {
  const snap = await getDocs(query(collection(db, "postComments"), orderBy("createdAt", "desc")));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};
export const updatePostComment = async (id, data) => updateDoc(doc(db, "postComments", id), data);
export const replyToPostComment = async (id, reply) =>
  updateDoc(doc(db, "postComments", id), { reply: reply.trim(), repliedAt: reply.trim() ? serverTimestamp() : null, status: "approved" });
export const deletePostComment = async (id) => deleteDoc(doc(db, "postComments", id));

export const getAllPostReactions = async () => {
  const snap = await getDocs(collection(db, "postReactions"));
  return Object.fromEntries(snap.docs.map(d => [d.id, { up: d.data().up || 0, down: d.data().down || 0 }]));
};

export const getAllPostFeedback = async () => {
  const snap = await getDocs(query(collection(db, "postFeedback"), orderBy("createdAt", "desc")));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};
export const markPostFeedbackRead = async (id, read = true) => updateDoc(doc(db, "postFeedback", id), { read });
export const deletePostFeedback = async (id) => deleteDoc(doc(db, "postFeedback", id));

/** Small counts for the admin nav badge — pending comments + unread feedback. */
export const getBlogAttentionCount = async () => {
  try {
    const [pending, unread] = await Promise.all([
      getDocs(query(collection(db, "postComments"), where("status", "==", "pending"))),
      getDocs(query(collection(db, "postFeedback"), where("read", "==", false))),
    ]);
    return pending.size + unread.size;
  } catch { return 0; }
};

export const setPostVisibility = async (id, visible) =>
  updateDoc(doc(db, "posts", id), {
    visible,
    updatedAt: serverTimestamp(),
    ...(visible ? { publishedAt: serverTimestamp() } : {}),
  });

// ─── PROFILE ──────────────────────────────────────────────────────────────────

export const getProfile = async () => {
  try {
    const snap = await getDoc(doc(db, "profile", "main"));
    if (snap.exists()) return { id: "main", ...snap.data() };
  } catch {}
  return { id: "main", ...fallback.profile };
};

export const updateProfile = async (data, photoFile) => {
  let photo = data.photo || "";
  if (photoFile) photo = await uploadImage(photoFile, "portfolio/profile");
  const { photo: _, id: _id, ...rest } = data;
  return setDoc(doc(db, "profile", "main"), { ...rest, photo, updatedAt: serverTimestamp() }, { merge: true });
};

// ─── ABOUT ────────────────────────────────────────────────────────────────────

export const getAbout = async () => {
  try {
    const snap = await getDoc(doc(db, "about", "main"));
    if (snap.exists()) return { id: "main", ...snap.data() };
  } catch {}
  return { id: "main", ...fallback.about };
};

export const updateAbout = async (data) => {
  const { id: _, ...payload } = data;
  return setDoc(doc(db, "about", "main"), { ...payload, updatedAt: serverTimestamp() }, { merge: true });
};

// ─── SKILLS ───────────────────────────────────────────────────────────────────

export const getSkills = async () => {
  try {
    const snap = await getDoc(doc(db, "skills", "main"));
    if (snap.exists()) return (snap.data().categories || fallback.skills).filter(c => c.visible !== false);
  } catch {}
  return (fallback.skills || []).filter(c => c.visible !== false);
};

export const getSkillsAdmin = async () => {
  try {
    const snap = await getDoc(doc(db, "skills", "main"));
    if (snap.exists()) return snap.data().categories || fallback.skills;
  } catch {}
  return fallback.skills || [];
};

export const updateSkills = async (categories) =>
  setDoc(doc(db, "skills", "main"), { categories, updatedAt: serverTimestamp() }, { merge: true });

export const setSkillCategoryVisibility = async (index, visible) => {
  const snap = await getDoc(doc(db, "skills", "main"));
  if (!snap.exists()) return;
  const categories = [...(snap.data().categories || [])];
  if (!categories[index]) return;
  categories[index] = { ...categories[index], visible };
  return setDoc(doc(db, "skills", "main"), { categories, updatedAt: serverTimestamp() }, { merge: true });
};

// ─── SOCIALS ──────────────────────────────────────────────────────────────────

export const getSocials = async () => {
  try {
    const snap = await getDoc(doc(db, "config", "socials"));
    if (snap.exists()) return snap.data().links || fallback.socials;
  } catch {}
  return fallback.socials;
};

export const updateSocials = async (links) =>
  setDoc(doc(db, "config", "socials"), { links, updatedAt: serverTimestamp() }, { merge: true });

// ─── RESUME DATA ──────────────────────────────────────────────────────────────
// getResumeData merges the live experience collection into the resume payload
// so ResumeGenerator always receives up-to-date experience entries.

export const getResumeData = async () => {
  try {
    const [resumeSnap, expDocs] = await Promise.all([
      getDoc(doc(db, "resume", "main")),
      getExperience(),
    ]);

    const base = resumeSnap.exists()
      ? { id: "main", ...resumeSnap.data() }
      : { id: "main", ...fallback.resume };

    // Replace the embedded experience array with the live collection data.
    // If the collection is empty (first run / migration not done), fall back
    // to whatever is stored in resume/main.
    return {
      ...base,
      experience: expDocs.length > 0 ? expDocs : (base.experience || []),
    };
  } catch {
    return { id: "main", ...fallback.resume };
  }
};

export const updateResumeData = async (data) => {
  const { id: _, ...payload } = data;
  // Never write the experience array back into resume/main — it lives in the collection.
  const { experience: _exp, ...safePayload } = payload;
  return setDoc(doc(db, "resume", "main"), { ...safePayload, updatedAt: serverTimestamp() }, { merge: true });
};

// ─── RESUME APPLICATIONS ──────────────────────────────────────────────────────

// Private contact details (full postal address) — resume/contact, admin-only
// via the resume/{docId} rule. Kept OUT of resume/main on purpose: resume/main
// is sent to the AI for tailoring, and profile/main is publicly readable.
export const getPrivateContact = async () => {
  try {
    const snap = await getDoc(doc(db, "resume", "contact"));
    return snap.exists() ? snap.data() : {};
  } catch { return {}; }
};

export const updatePrivateContact = async (data) =>
  setDoc(doc(db, "resume", "contact"), { ...data, updatedAt: serverTimestamp() }, { merge: true });

export const getResumeApplications = async () => {
  try {
    const q    = query(collection(db, "resumeApplications"), orderBy("appliedAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch { return []; }
};

/**
 * Save a resume application with full match criteria data
 * @param {Object} data - Application data
 * @param {string} data.role - Job role/title
 * @param {string} data.company - Company name
 * @param {string} data.jobDescription - Full job description text
 * @param {string} data.jobUrl - URL to job posting
 * @param {Object} data.tailoredResumeData - Tailored resume JSON
 * @param {string} data.coverLetter - Cover letter text
 * @param {Array} data.visibleSections - Enabled resume sections
 * @param {Object} data.skillsAnalysis - Skills gap analysis (matched/missing skills)
 * @param {string} data.generatedPrompt - The AI prompt used
 * @param {string} data.passportPhoto - Base64 photo data
 * @param {string} data.status - Application status (Applied, Interview, etc.)
 * @param {string} data.notes - User notes
 * @param {Object} data.strengthBreakdown - Full application strength breakdown
 * @param {number} data.strengthBreakdown.total - Overall strength score
 * @param {number} data.strengthBreakdown.skillMatch - Skill match percentage
 * @param {number} data.strengthBreakdown.coverage - Section coverage percentage
 * @param {number} data.strengthBreakdown.expRelevance - Experience relevance percentage
 * @param {number} data.strengthBreakdown.coverLetterScore - Cover letter score
 * @param {boolean} data.strengthBreakdown.aiScored - Whether AI scored it
 * @param {Object} data.extractedJobDetails - Auto-extracted job details
 * @param {string} data.extractedJobDetails.role - Extracted role
 * @param {string} data.extractedJobDetails.company - Extracted company
 */
export const saveResumeApplication = async ({
  role, company, jobDescription, jobUrl = "",
  tailoredResumeData, coverLetter = "", visibleSections = [],
  skillsAnalysis = null, generatedPrompt = "", passportPhoto = "",
  address = "",               // full postal address, only when this application included it
  status = "Applied", notes = "",
  strengthBreakdown = null,  // ★ NEW: Full strength breakdown
  extractedJobDetails = null, // ★ NEW: Auto-extracted job details
  strengthScore = null,       // ★ NEW: For backward compatibility
}) => {
  return addDoc(collection(db, "resumeApplications"), {
    role, 
    company, 
    jobDescription, 
    jobUrl,
    tailoredResumeData, 
    coverLetter, 
    visibleSections,
    skillsAnalysis, 
    generatedPrompt, 
    passportPhoto,
    address,
    status, 
    notes,
    // ★ NEW FIELDS
    strengthBreakdown,      // Full match criteria breakdown
    extractedJobDetails,    // Auto-extracted role & company
    strengthScore,          // Quick reference score
    appliedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const updateResumeApplication = async (id, data) => {
  const { id: _, ...payload } = data;
  return updateDoc(doc(db, "resumeApplications", id), { ...payload, updatedAt: serverTimestamp() });
};

export const deleteResumeApplication = async (id) =>
  deleteDoc(doc(db, "resumeApplications", id));

/**
 * Get application statistics for dashboard
 * @returns {Promise<Object>} Statistics about applications
 */
export const getApplicationStats = async () => {
  try {
    const apps = await getResumeApplications();
    const stats = {
      total: apps.length,
      byStatus: {},
      averageStrength: null,
      topMatches: [],
    };
    
    let totalStrength = 0;
    let strengthCount = 0;
    
    apps.forEach(app => {
      // Count by status
      stats.byStatus[app.status] = (stats.byStatus[app.status] || 0) + 1;
      
      // Calculate average strength
      if (app.strengthScore) {
        totalStrength += app.strengthScore;
        strengthCount++;
      }
      
      // Track top matches
      if (app.strengthScore && app.strengthScore >= 80) {
        stats.topMatches.push({
          role: app.role,
          company: app.company,
          strength: app.strengthScore,
          date: app.appliedAt,
          id: app.id,
        });
      }
    });
    
    stats.averageStrength = strengthCount > 0 ? Math.round(totalStrength / strengthCount) : null;
    stats.topMatches = stats.topMatches.sort((a, b) => b.strength - a.strength).slice(0, 5);
    
    return stats;
  } catch {
    return { total: 0, byStatus: {}, averageStrength: null, topMatches: [] };
  }
};

// ─── CONTACT SUBMISSIONS ──────────────────────────────────────────────────────

export const saveContactSubmission = async ({ name, email, subject, message, emailSent, emailError = "" }) =>
  addDoc(collection(db, "contactSubmissions"), {
    name, email, subject, message, emailSent, emailError,
    read: false, submittedAt: serverTimestamp(),
  });

export const getContactSubmissions = async () => {
  try {
    const q    = query(collection(db, "contactSubmissions"), orderBy("submittedAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch { return []; }
};

export const markSubmissionRead = async (id, read = true) =>
  updateDoc(doc(db, "contactSubmissions", id), { read, updatedAt: serverTimestamp() });

export const deleteContactSubmission = async (id) =>
  deleteDoc(doc(db, "contactSubmissions", id));

// ─── ADMIN NOTES ──────────────────────────────────────────────────────────────

export const getAdminNotes = async () => {
  try {
    const q    = query(collection(db, "adminNotes"), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch { return []; }
};

export const addAdminNote = async (note, author) =>
  addDoc(collection(db, "adminNotes"), {
    note, author,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

export const deleteAdminNote = async (id) =>
  deleteDoc(doc(db, "adminNotes", id));

export const updateAdminNote = async (id, note) =>
  updateDoc(doc(db, "adminNotes", id), { note, updatedAt: serverTimestamp() });

// ─── EXPENSES ─────────────────────────────────────────────────────────────────
// Private financial log. Each document:
//   { amount, currency, type: "expense"|"income", category, description,
//     date: "YYYY-MM-DD", location: "hungary"|"kenya"|"other", paymentMethod,
//     source: "manual"|"receipt"|"mpesa", externalId, receiptUrl,
//     extractionRaw, reviewed }

export const getExpensesAdmin = async () => {
  const q    = query(collection(db, "expenses"), orderBy("date", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const addExpense = async (data) =>
  addDoc(collection(db, "expenses"), {
    ...data,
    source:        data.source || "manual",
    externalId:    data.externalId || null,
    receiptUrl:    data.receiptUrl || null,
    extractionRaw: data.extractionRaw || null,
    reviewed:      data.reviewed !== false,
    createdAt:     serverTimestamp(),
    updatedAt:     serverTimestamp(),
  });

export const updateExpense = async (id, data) =>
  updateDoc(doc(db, "expenses", id), { ...data, updatedAt: serverTimestamp() });

export const deleteExpense = async (id) => deleteDoc(doc(db, "expenses", id));

/** Bulk-write parsed statement transactions in one commit (M-Pesa import). */
export const addExpensesBatch = async (items) => {
  const batch = writeBatch(db);
  items.forEach(item => {
    const ref = doc(collection(db, "expenses"));
    batch.set(ref, {
      ...item,
      externalId:    item.externalId || null,
      receiptUrl:    item.receiptUrl || null,
      extractionRaw: item.extractionRaw || null,
      reviewed:      item.reviewed !== false,
      createdAt:     serverTimestamp(),
      updatedAt:     serverTimestamp(),
    });
  });
  await batch.commit();
};

/** Firebase-Storage-only upload for receipts/statements (images + PDFs, not just images). */
export const uploadExpenseDocument = async (file) => uploadViaFirebase(file, "expenses");

/**
 * Which of these M-Pesa transaction codes already exist as expenses — used to
 * avoid double-importing a statement that overlaps a previous import.
 * Firestore's `in` operator caps at 30 values per query, so this batches.
 */
export const getExpensesByExternalIds = async (ids) => {
  const unique = [...new Set(ids.filter(Boolean))];
  if (!unique.length) return [];
  const chunks = [];
  for (let i = 0; i < unique.length; i += 30) chunks.push(unique.slice(i, i + 30));
  const results = await Promise.all(chunks.map(async (chunk) => {
    const q = query(collection(db, "expenses"), where("externalId", "in", chunk));
    const snap = await getDocs(q);
    return snap.docs.map(d => d.data().externalId);
  }));
  return results.flat();
};

// ─── EXPENSE BUDGETS ────────────────────────────────────────────────────────
// One doc per budget definition (not a single settings doc), so multiple
// concurrent budgets can coexist — e.g. "Food – HUF – monthly" and
// "Family support – KES – monthly". Each document:
//   { label, category: string|null (null = whole-currency budget), currency,
//     amount, period: "monthly"|"weekly", alertThreshold, active }

export const getBudgetsAdmin = async () => {
  const snap = await getDocs(collection(db, "expenseBudgets"));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const addBudget = async (data) =>
  addDoc(collection(db, "expenseBudgets"), {
    ...data,
    active: data.active !== false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

export const updateBudget = async (id, data) =>
  updateDoc(doc(db, "expenseBudgets", id), { ...data, updatedAt: serverTimestamp() });

export const deleteBudget = async (id) => deleteDoc(doc(db, "expenseBudgets", id));

// ─── JOB ASSIST ─────────────────────────────────────────────────────────────
// jobPostings — one doc per posting, keyed by a DETERMINISTIC id built from
// (source, externalId) via buildJobDocId(). The id itself is the dedupe
// mechanism: scripts/fetchJobs.js checks existence before ever writing, so a
// posting already handled is never re-touched, re-scored, or re-tailored.
//   { source, sourceKey, externalId, title, company, location, remote, url,
//     postedAt, descriptionText, compensation,
//     keywordScore, aiScore, aiReasoning, matchTier, workflowStatus,
//     tailoredResumeData, coverLetter, resumeApplicationId, dismissedReason,
//     fetchedAt, updatedAt }
//
// jobSources — config for what scripts/fetchJobs.js polls, keyed by (type, key)
// so adding the same source twice from the UI just updates it.
//   { type, key, label, active, lastFetchedAt, lastFetchCount, lastNewCount,
//     lastError, createdAt, updatedAt }

export const getJobPostings = async () => {
  const q = query(collection(db, "jobPostings"), orderBy("fetchedAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const updateJobPosting = async (id, data) =>
  updateDoc(doc(db, "jobPostings", id), { ...data, updatedAt: serverTimestamp() });

export const deleteJobPosting = async (id) => deleteDoc(doc(db, "jobPostings", id));

/** Manual "paste a URL" / "paste JD text" add — skips the keyword pre-filter by design. */
export const addManualJobPosting = async (posting) => {
  const externalId = posting.externalId || hashString(posting.url || posting.descriptionText.slice(0, 500) + (posting.title || "") + (posting.company || ""));
  const id = buildJobDocId("manual", externalId);
  await setDoc(doc(db, "jobPostings", id), {
    source: "manual",
    sourceKey: "manual",
    externalId,
    title: posting.title || "",
    company: posting.company || "",
    location: posting.location || "",
    remote: !!posting.remote,
    url: posting.url || "",
    postedAt: posting.postedAt || null,
    descriptionText: (posting.descriptionText || "").slice(0, 20000),
    compensation: posting.compensation || null,
    keywordScore: null,
    aiScore: posting.aiScore ?? null,
    aiReasoning: posting.aiReasoning || null,
    matchTier: posting.matchTier || "uncertain",
    workflowStatus: posting.workflowStatus || "new",
    tailoredResumeData: posting.tailoredResumeData || null,
    coverLetter: posting.coverLetter || null,
    resumeApplicationId: null,
    dismissedReason: null,
    fetchedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return id;
};

export const getJobSources = async () => {
  const snap = await getDocs(collection(db, "jobSources"));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const upsertJobSource = async (source) => {
  const id = buildJobSourceDocId(source.type, source.key);
  await setDoc(doc(db, "jobSources", id), {
    type: source.type,
    key: source.key,
    label: source.label || "",
    active: source.active !== false,
    updatedAt: serverTimestamp(),
    ...(source.createdAt ? {} : { createdAt: serverTimestamp() }),
  }, { merge: true });
  return id;
};

export const deleteJobSource = async (id) => deleteDoc(doc(db, "jobSources", id));

// Job Assist search preferences (title/location filters, candidate context,
// auto-refresh) — read by src/shared/jobsFetcher.js at the start of each run.
export const getJobSearchSettings = async () => {
  const snap = await getDoc(doc(db, "jobSearchSettings", "main"));
  return snap.exists() ? snap.data() : null;
};

export const saveJobSearchSettings = async (prefs) =>
  setDoc(doc(db, "jobSearchSettings", "main"), { ...prefs, updatedAt: serverTimestamp() }, { merge: true });

/** Written by every fetch run (button or cron) — { ranAt, ranAtTs, sources, aiCallsUsed, note? }. */
export const getJobLastRun = async () => {
  const snap = await getDoc(doc(db, "jobSearchSettings", "lastRun"));
  return snap.exists() ? snap.data() : null;
};
